import './App.css';
import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import Aos from 'aos';
import Toast from './components/Notifications/Toast';
import BigLoader from './components/Notifications/BigLoader';
import { ProtectedRouter } from './ProtectedRouter';
import { useSelector } from 'react-redux';
import SplashScreen from './screens/Splash/SplashScreen';

const SMSCredentials = React.lazy(() => import('./screens/e-solutions/ESolutionsSmsCredentials'));
const EmailPackages = React.lazy(() => import('./screens/email-packages/EmailPackages'));
const SMSPackages = React.lazy(() => import('./screens/sms-packages/SMSPackages'));
const HowToGetStarted = React.lazy(() => import('./screens/GettingStarted'));
const Subscriptions = React.lazy(() => import('./screens/subscriptions/Subscriptions'));
const Packages = React.lazy(() => import('./screens/packages/Packages'));
const Application = React.lazy(() => import('./screens/applications/Applications'));
const Smses = React.lazy(() => import('./screens/sms/SmsPage'));
const Emails = React.lazy(() => import('./screens/emails/EmailsPage'));
const MailingLists = React.lazy(() => import('./screens/mailinglist/MailingListPage'));
const MailingListEntries = React.lazy(() => import('./screens/mailinglist/MailingListEntriesPage'));

const ApiDocumentation = React.lazy(() => import('./screens/developers/ApiDocumentation'));
const SDKAndLibraries = React.lazy(() => import('./screens/developers/SDKAndLibraries'));
const APIReference = React.lazy(() => import('./screens/developers/APIReference'));
const MyAccount = React.lazy(() => import('./screens/Settings'));

const Dashboard = React.lazy(() => import('./screens/Dashboard'));
const Profile = React.lazy(() => import('./screens/Settings'));
const NotFound = React.lazy(() => import('./screens/NotFound'));
const Login = React.lazy(() => import('./screens/Login'));
const Verification = React.lazy(() => import('./screens/Verification'));
const ResendVerification = React.lazy(() => import('./screens/ResendVerification'));
const RegistrationSuccess = React.lazy(() => import('./screens/RegistrationSuccess'));
const Register = React.lazy(() => import('./screens/Register'));
const ForgotPassword = React.lazy(() => import('./screens/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./screens/ResetPassword'));

const Transactions = React.lazy(() => import('./screens/transactions/Transactions'));
const PaymentResults = React.lazy(() => import('./screens/PaymentsResult'));


function App() {
  const [isSplashVisible, setSplashVisible] = useState(true);
  const userInfo = useSelector((state) => state.userLogin.userInfo);
  const navigate = useNavigate();
  const location = useLocation();
 
  // Initialize AOS
  useEffect(() => {
    Aos.init();
  }, []);



  useEffect(() => {
    if (location.pathname !== '/verification') {
      const splashShown = sessionStorage.getItem('splashShown');
      if (!splashShown) {
        const timer = setTimeout(() => {
          setSplashVisible(false);
          sessionStorage.setItem('splashShown', 'true');
        }, 3000);
        return () => clearTimeout(timer);
      } else {
        setSplashVisible(false);
      }
    } else {
      setSplashVisible(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = window.location.pathname;

    if (!userInfo || !userInfo?.jwtToken) {
      if (
        currentPath !== '/register' &&
        currentPath !== '/verification' &&
        currentPath !== '/register-success' &&
        currentPath !== '/resend-verification' &&
        currentPath !== '/forgot-password' &&
        currentPath !== '/reset-password'
      ) {
        navigate('/login');
      }
    }
  }, [navigate, userInfo, location.pathname]);


  useEffect(() => {
    Aos.init();
    const timer = setTimeout(() => setSplashVisible(false), 3000);
    return () => clearTimeout(timer);
  }, []);


  if (isSplashVisible && (location.pathname !== '/verification' || location.pathname !== '/register-success' || location.pathname !== '/resend-verification')) {
    return <SplashScreen />;
  }



  return (
    <>
      {/* Toaster */}
      <Toast />
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<BigLoader />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<BigLoader />}>
              <Register />
            </Suspense>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<BigLoader />}>
              <ForgotPassword />
            </Suspense>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<BigLoader />}>
              <ResetPassword />
            </Suspense>
          }
        />
        <Route
          path="/verification"
          element={
            <Suspense fallback={<BigLoader />}>
              <Verification />
            </Suspense>
          }
        />

        <Route
          path="/resend-verification"
          element={
            <Suspense fallback={<BigLoader />}>
              <ResendVerification />
            </Suspense>
          }
        />

        <Route
          path="/register-success"
          element={
            <Suspense fallback={<BigLoader />}>
              <RegistrationSuccess />
            </Suspense>
          }
        />


        {/* PROTECTED */}
        <Route element={<ProtectedRouter />}>

          <Route
            path="/how-to-get-started"
            element={
              <Suspense fallback={<BigLoader />}>
                <HowToGetStarted />
              </Suspense>
            }
          />

          <Route
            path="/"
            element={
              <Suspense fallback={<BigLoader />}>
                <Dashboard />
              </Suspense>
            }
          />

          <Route
            path="/subscriptions"
            element={
              <Suspense fallback={<BigLoader />}>
                <Subscriptions />
              </Suspense>
            }
          />

          <Route
            path="/packages"
            element={
              <Suspense fallback={<BigLoader />}>
                <Packages />
              </Suspense>
            }
          />
          <Route
            path="/applications"
            element={
              <Suspense fallback={<BigLoader />}>
                <Application />
              </Suspense>
            }
          />
          <Route
            path="/smses/messages"
            element={
              <Suspense fallback={<BigLoader />}>
                <Smses />
              </Suspense>
            }
          />
          <Route
            path="/emails/messages"
            element={
              <Suspense fallback={<BigLoader />}>
                <Emails />
              </Suspense>
            }
          />
          <Route
            path="/emails/mailing-list"
            element={
              <Suspense fallback={<BigLoader />}>
                <MailingLists />
              </Suspense>
            }
          />
          
          <Route
            path="/mailing-lists/:id/entries"
            element={
              <Suspense fallback={<BigLoader />}>
                <MailingListEntries />
              </Suspense>
            }
          />
          <Route
            path="/my-account"
            element={
              <Suspense fallback={<BigLoader />}>
                <MyAccount />
              </Suspense>
            }
          />
          <Route
            path="/profile"
            element={
              <Suspense fallback={<BigLoader />}>
                <Profile />
              </Suspense>
            }
          />
          <Route
            path="/transactions"
            element={
              <Suspense fallback={<BigLoader />}>
                <Transactions />
              </Suspense>
            }
          />

          <Route
            path="/sms-credentials/e-solutions"
            element={
              <Suspense fallback={<BigLoader />}>
                <SMSCredentials />
              </Suspense>
            }
          />

          <Route
            path="/emails/packages"
            element={
              <Suspense fallback={<BigLoader />}>
                <EmailPackages />
              </Suspense>
            }
          />

          <Route
            path="/smses/packages"
            element={
              <Suspense fallback={<BigLoader />}>
                <SMSPackages />
              </Suspense>
            }
          />

          <Route
            path="/developers/documentation"
            element={
              <Suspense fallback={<BigLoader />}>
                <ApiDocumentation />
              </Suspense>
            }
          />

          <Route
            path="/developers/sdks"
            element={
              <Suspense fallback={<BigLoader />}>
                <SDKAndLibraries />
              </Suspense>
            }
          />

          <Route
            path="/developers/api-reference"
            element={
              <Suspense fallback={<BigLoader />}>
                <APIReference />
              </Suspense>
            }
          />
          <Route
            path="/payment-results"
            element={
              <Suspense fallback={<BigLoader />}>
                <PaymentResults />
              </Suspense>
            }
          />
          


        </Route>
        <Route
          path="*"
          element={
            <Suspense fallback={<BigLoader />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
}

export default App;